import type { ReactNode } from "react";
import { ChevronLeft, ChevronRight, X, type LucideIcon } from "lucide-react";
import { DatePicker } from "@/components/ui/date-picker";
import { formatDate, formatTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";

export const TH = "text-[10px] uppercase tracking-[0.14em]";

export function TableShell({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn("overflow-x-auto rounded-xl border bg-card/60 backdrop-blur-sm", className)}>
      {children}
    </div>
  );
}

export function HeaderRow({ children }: { children: ReactNode }) {
  return <TableRow className="bg-muted/40 hover:bg-muted/40">{children}</TableRow>;
}

/** Props that make a table row behave as a link: clickable, focusable, and opened with Enter. */
export function rowLink(onOpen?: () => void) {
  if (!onOpen) return {};
  return {
    role: "link",
    tabIndex: 0,
    className: "cursor-pointer focus-visible:bg-muted/50 focus-visible:outline-none",
    onClick: onOpen,
    onKeyDown: (e: React.KeyboardEvent) => {
      // Enter on a link or button inside the row belongs to that control, not the row. Only a key press
      // on the row itself opens it.
      if (e.key === "Enter" && e.target === e.currentTarget) onOpen();
    },
  };
}

export function Eyebrow({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <p className={cn("text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground", className)}>
      {children}
    </p>
  );
}

/** No avatar: monograms add noise, so avatars are reserved for the account menu. */
export function EntityCell({
  name,
  secondary,
  badges,
  fallback = "Unnamed",
}: {
  name?: string | null;
  secondary?: ReactNode;
  badges?: ReactNode;
  fallback?: string;
}) {
  const display = name?.trim() || fallback;
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1.5">
        <span className="truncate font-semibold">{display}</span>
        {badges}
      </div>
      {secondary ? <div className="truncate text-xs text-muted-foreground">{secondary}</div> : null}
    </div>
  );
}

export function EmptyState({
  icon: Icon,
  title,
  children,
  action,
}: {
  icon: LucideIcon;
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center rounded-xl border border-dashed bg-card/30 px-6 py-14 text-center">
      <span className="grid size-10 place-items-center rounded-xl border bg-muted/50 text-muted-foreground">
        <Icon className="size-5" />
      </span>
      <p className="mt-3 text-sm font-semibold">{title}</p>
      {children ? <p className="mt-1 max-w-md text-sm text-pretty text-muted-foreground">{children}</p> : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

export function LoadingState({ label }: { label: string }) {
  return (
    <div className="flex items-center justify-center gap-2 rounded-xl border bg-card/30 py-16 text-sm text-muted-foreground">
      <Spinner className="size-4" /> {label}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-xl border border-destructive/30 bg-destructive/5 py-12 text-center text-sm">
      <p className="text-destructive">{message}</p>
      {onRetry ? (
        <Button variant="outline" size="sm" onClick={onRetry}>
          Try again
        </Button>
      ) : null}
    </div>
  );
}

export function Pager({
  page,
  hasPrevious,
  hasNext,
  onPrevious,
  onNext,
  disabled = false,
}: {
  page: number;
  hasPrevious: boolean;
  hasNext: boolean;
  onPrevious: () => void;
  onNext: () => void;
  /** True while a page is loading: a second click before the first lands would skip or repeat a page. */
  disabled?: boolean;
}) {
  if (!hasPrevious && !hasNext) return null;
  return (
    <div className="flex items-center justify-between pt-3">
      <span className="text-xs text-muted-foreground tabular-nums">Page {page}</span>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={onPrevious} disabled={disabled || !hasPrevious}>
          <ChevronLeft className="size-4" /> Previous
        </Button>
        <Button variant="outline" size="sm" onClick={onNext} disabled={disabled || !hasNext}>
          Next <ChevronRight className="size-4" />
        </Button>
      </div>
    </div>
  );
}

export interface DateRange {
  from?: string;
  to?: string;
}

/** The API's To is an instant, so a bare date would drop that whole day; widen the picked days to cover them. */
export function dateRangeQuery(range: DateRange) {
  const query: { From?: string; To?: string } = {};
  if (range.from) query.From = `${range.from}T00:00:00Z`;
  if (range.to) query.To = `${range.to}T23:59:59.999Z`;
  return query;
}

export function DateRangeFilter({
  value,
  onChange,
}: {
  value: DateRange;
  onChange: (value: DateRange) => void;
}) {
  const active = !!(value.from || value.to);
  return (
    <div className="flex items-center gap-1.5">
      <DatePicker
        value={value.from}
        onChange={(from) => onChange({ ...value, from: from ?? undefined })}
        placeholder="From"
        className="h-9 w-36"
      />
      <span className="text-xs text-muted-foreground">to</span>
      <DatePicker
        value={value.to}
        onChange={(to) => onChange({ ...value, to: to ?? undefined })}
        placeholder="To"
        className="h-9 w-36"
      />
      {active ? (
        <Button variant="ghost" size="icon" className="size-8" aria-label="Clear dates" onClick={() => onChange({})}>
          <X className="size-4" />
        </Button>
      ) : null}
    </div>
  );
}

// A bar, not a bare percentage: "clearly the one" versus "one of several" reads at a glance.
export function ScoreMeter({ score }: { score: number }) {
  const pct = Math.max(0, Math.min(100, Math.round(score * 100)));
  return (
    <div className="hidden w-20 shrink-0 @md:block" aria-label={`${pct}% match`}>
      <div className="h-1.5 overflow-hidden rounded-full bg-muted">
        <div
          className={cn("h-full rounded-full", pct >= 80 ? "bg-emerald-500" : pct >= 50 ? "bg-accent-blue" : "bg-muted-foreground/50")}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="mt-1 text-right text-[10px] text-muted-foreground tabular-nums">{pct}% match</div>
    </div>
  );
}

export interface TimelineEntry {
  id: string;
  title: ReactNode;
  at?: string | null;
  detail?: ReactNode;
}

/** Newest first. Callers map audit events to words; event codes are the API's vocabulary. */
export function Timeline({ entries }: { entries: TimelineEntry[] }) {
  return (
    <ol className="relative">
      {entries.map((entry, i) => (
        <li key={entry.id} className="relative flex gap-4 pb-5 text-sm last:pb-0">
          {i < entries.length - 1 ? (
            <span aria-hidden className="absolute top-3 left-[5px] h-full w-px bg-border" />
          ) : null}
          <span
            aria-hidden
            className={cn(
              "relative mt-1.5 size-[11px] shrink-0 rounded-full border-2",
              i === 0 ? "border-accent-blue bg-accent-blue" : "border-border bg-background",
            )}
          />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-baseline justify-between gap-x-3">
              <span className="font-medium">{entry.title}</span>
              {entry.at ? (
                <span className="text-xs text-muted-foreground tabular-nums">
                  {formatDate(entry.at)} · {formatTime(entry.at)}
                </span>
              ) : null}
            </div>
            {entry.detail ? <div className="mt-0.5 text-muted-foreground">{entry.detail}</div> : null}
          </div>
        </li>
      ))}
    </ol>
  );
}
